import type { ReactElement } from 'react';
import { Badge, Box, Button, ScrollArea, Stack, Text, TextInput } from '@mantine/core';
import { Plus, Search } from 'lucide-react';
import type { Classification } from './ClassificationService';
import { statusMeta, type CategoryNoun } from './ClassificationOptions';

interface ClassificationListRailProps {
    classifications: Classification[];
    selectedId: string | null;
    searchQuery: string;
    noun: CategoryNoun;
    onSearch: (value: string) => void;
    onSelect: (id: string) => void;
    onNew: () => void;
    /** When true (small screens), the rail spans the full width as the sole visible pane (master-detail). */
    stacked?: boolean;
}

export default function ClassificationListRail({
    classifications,
    selectedId,
    searchQuery,
    noun,
    onSearch,
    onSelect,
    onNew,
    stacked = false,
}: ClassificationListRailProps): ReactElement {
    const pluralLower = noun.plural.toLowerCase();
    return (
        <Box
            style={{
                width: stacked ? '100%' : 300,
                flexShrink: 0,
                borderRight: stacked ? undefined : '1px solid var(--bm-color-border)',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            <Stack gap="sm" p="md" style={{ borderBottom: '1px solid var(--bm-color-border)' }}>
                <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text fw={800} size="sm">
                        {noun.plural}
                    </Text>
                    <Button size="compact-sm" leftSection={<Plus size={14} />} onClick={onNew}>
                        New
                    </Button>
                </Box>
                <TextInput
                    size="xs"
                    placeholder={`Search ${pluralLower}…`}
                    leftSection={<Search size={14} />}
                    value={searchQuery}
                    onChange={(e) => onSearch(e.currentTarget.value)}
                />
            </Stack>

            <ScrollArea style={{ flex: 1 }}>
                <Stack gap={0} py="xs">
                    {classifications.map((c) => {
                        const active = c.id === selectedId;
                        return (
                            <Box
                                key={c.id}
                                onClick={() => onSelect(c.id)}
                                style={{
                                    cursor: 'pointer',
                                    padding: '10px 16px',
                                    borderLeft: active ? '2.5px solid var(--mantine-primary-color-filled)' : '2.5px solid transparent',
                                    background: active ? 'var(--mantine-primary-color-light)' : 'transparent',
                                }}
                            >
                                <Text size="sm" fw={600} lineClamp={1}>
                                    {c.name || `Untitled ${noun.singular.toLowerCase()}`}
                                </Text>
                                <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                                    <Text size="xs" c="dimmed">
                                        {c.category}
                                    </Text>
                                    <Badge size="xs" variant="light" color={statusMeta(c.status).color}>
                                        {statusMeta(c.status).label}
                                    </Badge>
                                </Box>
                            </Box>
                        );
                    })}
                    {classifications.length === 0 && (
                        <Text size="xs" c="dimmed" p="md">
                            No {pluralLower} yet. Create one to get started.
                        </Text>
                    )}
                </Stack>
            </ScrollArea>
        </Box>
    );
}
